import { Seo } from "../components/Seo";
import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Target, Eye } from "lucide-react";
import { ABOUT, FINAL_CTA, INFO_EMAIL } from "../mock";
import { PageHero, Reveal, SectionHeader, DarkCTA } from "../components/Shared";

const LogoRow = ({ label, logos, testid }) => (
  <div data-testid={testid}>
    <div className="eyebrow">{label}</div>
    <div className="mt-5 flex flex-wrap items-center gap-x-12 gap-y-6">
      {logos.map((l) => (
        <a
          key={l.name}
          href={l.href}
          target="_blank"
          rel="noreferrer"
          title={l.name}
          className="inline-flex items-center"
          data-testid={`backer-${l.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          <img src={l.src} alt={l.name} loading="lazy" className="client-logo" style={{ height: 40 }} />
        </a>
      ))}
    </div>
  </div>
);

const About = () => (
  <>
    <Seo title="About Fleetroot — From an idea to a regional mobility platform" description="Founded in 2021, Fleetroot helps businesses connect their vehicles, drivers and deliveries through one connected platform. Backed by Flat6Labs, MBRIF, TASMU and Microsoft for Startups." />
    <PageHero eyebrow={ABOUT.eyebrow} title={ABOUT.title} subtitle={ABOUT.text} primary={{ label: "Talk to us", to: "/contact" }}>
      <div className="mt-12 flex flex-wrap gap-8 md:gap-12 fade-up delay-4" data-testid="about-stats">
        {ABOUT.stats.map((s) => (
          <div key={s.label} className="stat">
            <div className="font-display text-3xl md:text-4xl font-bold tracking-tight">{s.value}</div>
            <div className="text-sm text-[var(--fr-muted)] mt-1">{s.label}</div>
          </div>
        ))}
      </div>
    </PageHero>

    {/* Story + team photos */}
    <section className="section" data-testid="about-story">
      <div className="container-x grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <span className="eyebrow">What we do</span>
          <h2 className="h-section mt-4">Inspiring solutions, transforming lives.</h2>
          <div className="mt-8 space-y-4" data-testid="about-mission">
            {[
              { icon: Target, ...ABOUT.mission },
              { icon: Eye, ...ABOUT.vision },
            ].map((m) => (
              <div key={m.title} className="card card-hover p-6 flex gap-5">
                <div className="w-10 h-10 rounded-xl bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)] flex items-center justify-center flex-shrink-0">
                  <m.icon size={20} />
                </div>
                <div>
                  <h3 className="h-card">{m.title}</h3>
                  <p className="mt-2 text-[15px] text-[var(--fr-ink-2)] leading-relaxed">{m.text}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={120} className="grid grid-cols-[1fr_1fr] gap-3">
          <div className="card p-2 col-span-2">
            <div className="img-tile aspect-[3/2]">
              <img src={ABOUT.teamPhotos[0]} alt="The Fleetroot team at the Dubai office" loading="lazy" />
            </div>
          </div>
          <div className="card p-2">
            <div className="img-tile aspect-[4/3]">
              <img src={ABOUT.teamPhotos[1]} alt="Fleetroot team at an industry exhibition" loading="lazy" />
            </div>
          </div>
          <div className="dark-block !rounded-[16px] p-5 flex flex-col justify-between">
            <div className="text-xs uppercase tracking-[0.14em] text-white/60">Since March 2021</div>
            <div>
              <div className="font-display text-3xl font-bold tracking-tight">50+ yrs</div>
              <div className="text-sm text-white/70 mt-1">collective mobility experience</div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    {/* Backed by / Supported by */}
    <section className="py-14 border-y border-[var(--fr-border)] bg-[var(--fr-bg-alt)]" data-testid="about-backers">
      <div className="container-x">
        <Reveal className="mb-8">
          <span className="eyebrow">Who backs us</span>
          <h2 className="h-card mt-3 text-xl">Backed by leading accelerators and innovation programs.</h2>
        </Reveal>
        <div className="grid md:grid-cols-[1fr_2fr] gap-10 md:gap-16">
          <Reveal>
            <LogoRow label="Backed by" logos={ABOUT.backedBy} testid="backed-by" />
          </Reveal>
          <Reveal delay={100} className="md:border-l md:border-[var(--fr-border)] md:pl-16">
            <LogoRow label="Supported by" logos={ABOUT.supportedBy} testid="supported-by" />
          </Reveal>
        </div>
      </div>
    </section>

    {/* Team */}
    <section className="section" data-testid="about-team">
      <div className="container-x">
        <SectionHeader eyebrow="Team" title={ABOUT.teamIntro.title} subtitle={ABOUT.teamIntro.text} />
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {ABOUT.team.map((m, i) => (
            <Reveal key={m.name} delay={(i % 5) * 60} className="card card-hover p-2 team-card" data-testid="team-member">
              <div className="img-tile aspect-square">
                <img src={m.img} alt={m.name} loading="lazy" />
              </div>
              <div className="px-2 pt-3 pb-2">
                <div className="font-display font-bold text-[15px] leading-tight">{m.name}</div>
                <div className="text-xs text-[var(--fr-muted)] mt-1">{m.role}</div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={240} className="card p-2 flex">
            <div className="img-tile flex-1 bg-[var(--fr-primary-tint)] flex items-center justify-center text-center p-5">
              <div>
                <div className="font-display font-bold text-[var(--fr-primary-dark)] text-lg leading-tight">You?</div>
                <p className="text-xs text-[var(--fr-ink-2)] mt-2">We're hiring across product, ops and sales.</p>
                <Link to="/contact" className="mt-3 inline-block text-xs font-semibold text-[var(--fr-primary-dark)]">
                  Get in touch →
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* Milestones */}
    <section className="section bg-[var(--fr-bg-alt)]" data-testid="about-timeline">
      <div className="container-x grid lg:grid-cols-[1fr_2fr] gap-12">
        <SectionHeader eyebrow="Milestones" title="From an idea to a regional mobility platform" />
        <Reveal>
          <ol className="relative border-l border-[var(--fr-border)] ml-2">
            {ABOUT.milestones.map((m) => (
              <li key={m.year} className="pl-8 pb-9 relative last:pb-0" data-testid={`milestone-${m.year.replace(/\s+/g, "-").toLowerCase()}`}>
                <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-[var(--fr-primary)] ring-4 ring-[var(--fr-bg-alt)]" />
                <div className="text-[13px] font-semibold uppercase tracking-wide text-[var(--fr-primary-dark)]">{m.year}</div>
                <h3 className="mt-1 font-display text-lg font-bold text-[var(--fr-ink)]">{m.title}</h3>
                <p className="mt-1.5 text-[15px] text-[var(--fr-ink-2)]">{m.text}</p>
              </li>
            ))}
          </ol>
        </Reveal>
      </div>
    </section>

    {/* Offices */}
    <section className="section" data-testid="about-offices">
      <div className="container-x">
        <SectionHeader eyebrow="Where to find us" title="Our offices" />
        <div className="mt-12 grid md:grid-cols-2 gap-5">
          {ABOUT.offices.map((o, i) => (
            <Reveal key={o.entity} delay={(i % 2) * 80} className="card card-hover p-7" data-testid={`office-${i}`}>
              <div className="flex items-center justify-between gap-4">
                <span className="eyebrow-pill">{o.country}</span>
                <span className="font-display font-bold text-right">{o.entity}</span>
              </div>
              <ul className="mt-6 space-y-3 text-[15px]">
                <li className="flex items-start gap-3">
                  <MapPin size={16} className="mt-[3px] text-[var(--fr-primary-dark)] flex-shrink-0" />
                  <span className="text-[var(--fr-ink-2)]">{o.address}</span>
                </li>
                {o.phone && (
                  <li className="flex items-start gap-3">
                    <Phone size={16} className="mt-[3px] text-[var(--fr-primary-dark)] flex-shrink-0" />
                    <a href={o.phoneHref} className="font-semibold hover:text-[var(--fr-primary-dark)]">
                      {o.phone}
                    </a>
                  </li>
                )}
              </ul>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <DarkCTA
      title="Come build the future of delivery."
      text="Hundreds of businesses around the world run their direct deliveries on Fleetroot. Talk to the team or start free today."
      cta={FINAL_CTA.cta}
      secondary={{ label: "Contact us", to: "/contact" }}
      email={INFO_EMAIL}
    />
  </>
);

export default About;
